import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2 } from "lucide-react";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Session token is also kept in localStorage and sent as Bearer, so login keeps working
// when the browser blocks third-party cookies (custom domain + separate API host, Safari ITP).
const SESSION_KEY = "akis_session";
const applyAuthHeader = (token) => {
  if (token) axios.defaults.headers.common.Authorization = `Bearer ${token}`;
  else delete axios.defaults.headers.common.Authorization;
};
applyAuthHeader(localStorage.getItem(SESSION_KEY));

export const saveSession = (data) => {
  if (data?.session_token) {
    localStorage.setItem(SESSION_KEY, data.session_token);
    applyAuthHeader(data.session_token);
  }
  return data;
};

export const clearSession = () => {
  localStorage.removeItem(SESSION_KEY);
  applyAuthHeader(null);
};

const UserContext = createContext(null);
export const useUser = () => useContext(UserContext);

export const fmtError = (
  detail,
  fallback = "Bir hata oluştu, tekrar deneyin.",
) => {
  if (detail == null) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

export const startGoogleLogin = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/panel";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export const logoutRequest = () =>
  axios.post(`${API}/auth/logout`, {}).finally(clearSession);

/** Handles `#session_id=` returned by Emergent Google auth; exchanges it server-side, then lands on /panel. */
export const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const sessionId = new URLSearchParams(window.location.hash.slice(1)).get(
      "session_id",
    );
    axios
      .post(`${API}/auth/session`, { session_id: sessionId })
      .then(({ data }) =>
        navigate("/panel", {
          replace: true,
          state: { user: saveSession(data) },
        }),
      )
      .catch((err) =>
        navigate("/giris", {
          replace: true,
          state: {
            error: fmtError(
              err.response?.data?.detail,
              "Google girişi tamamlanamadı. Tekrar deneyin.",
            ),
          },
        }),
      );
  }, [navigate]);

  return (
    <div data-testid="auth-callback" className="min-h-screen bg-[#07090E]" />
  );
};

/** Server-verified gate: null = checking, user = ok, otherwise redirect to /giris. */
export const RequireAuth = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(location.state?.user || null);

  useEffect(() => {
    if (user) return;
    axios
      .get(`${API}/auth/me`)
      .then(({ data }) => setUser(data))
      .catch(() => {
        clearSession();
        navigate("/giris", {
          replace: true,
          state: { from: location.pathname },
        });
      });
  }, [user, navigate, location.pathname]);

  if (!user) {
    return (
      <div
        data-testid="auth-checking"
        className="flex min-h-screen items-center justify-center bg-[#07090E]"
      >
        <Loader2 className="h-6 w-6 animate-spin text-emerald-400" />
      </div>
    );
  }
  return (
    <UserContext.Provider value={{ user, setUser }}>
      {children}
    </UserContext.Provider>
  );
};
